import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  DataTable,
  ExportButtons,
  FilterBar,
  Pill,
  SearchInput,
  Section,
  StatCard,
  StatusBadge,
} from "@/components/admin/admin-kit";
import {
  admDeleteGroup,
  admEffectivePermissions,
  admGroups,
  admSaveGroup,
  admSetGrant,
  admSetGroupMember,
  admSetRole,
  admStaff,
} from "@/lib/admin-enterprise.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/access")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/access",
      "Access control | Control room",
      "Internal administration.",
    ),
  component: AdminAccess,
});

const PERMISSIONS = [
  "admin.all",
  "finance.view",
  "finance.manage",
  "payments.refund",
  "users.manage",
  "roles.manage",
  "listings.manage",
  "marketplace.manage",
  "marketplace.services.moderate",
  "marketplace.kyc.review",
  "support.manage",
  "notifications.send",
  "settings.manage",
] as const;

const ROLES = ["admin", "moderator"] as const;
const TABS = ["staff", "groups"] as const;

function AdminAccess() {
  const [tab, setTab] = useState<(typeof TABS)[number]>("staff");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <Pill key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </Pill>
        ))}
      </div>
      {tab === "staff" ? <StaffTab /> : <GroupsTab />}
    </div>
  );
}

/* --------------------------------- Staff -------------------------------- */

function StaffTab() {
  const qc = useQueryClient();
  const list = useServerFn(admStaff);
  const setRole = useServerFn(admSetRole);
  const perms = useServerFn(admEffectivePermissions);
  const [search, setSearch] = useState("");
  const [inspect, setInspect] = useState<string | null>(null);

  const { data = [], isLoading } = useQuery({ queryKey: ["adm-staff"], queryFn: () => list({ data: undefined }) });

  const { data: effective } = useQuery({
    queryKey: ["adm-effective", inspect],
    queryFn: () => perms({ data: { userId: inspect as string } }),
    enabled: Boolean(inspect),
  });

  const mut = useMutation({
    mutationFn: (input: { userId: string; role: "admin" | "moderator"; enabled: boolean }) =>
      setRole({ data: input }),
    onSuccess: () => {
      toast.success("Role updated.");
      void qc.invalidateQueries({ queryKey: ["adm-staff"] });
      void qc.invalidateQueries({ queryKey: ["adm-effective"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const rows = data.filter((s) =>
    search.trim()
      ? [s.display_name, s.handle, s.user_id]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(search.trim().toLowerCase()))
      : true,
  );

  const exportRows = rows.map((s) => ({
    member: s.display_name,
    handle: s.handle ?? "",
    roles: s.roles.join(" "),
    groups: s.groups.join(" "),
    suspended: String(s.is_suspended),
    user_id: s.user_id,
  }));

  return (
    <Section
      title="Staff & roles"
      description="Everyone holding a platform role or belonging to an admin group."
      actions={<ExportButtons rows={exportRows} filename="behot-staff" title="Staff" />}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatCard label="Staff members" value={rows.length} />
        <StatCard label="Admins" value={rows.filter((s) => s.roles.includes("admin")).length} tone="hot" />
        <StatCard
          label="Moderators"
          value={rows.filter((s) => s.roles.includes("moderator")).length}
          tone="ok"
        />
      </div>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Name, handle or id" />
      </FilterBar>
      {isLoading ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <DataTable
          rows={rows}
          rowKey={(r) => r.user_id}
          empty="No staff yet."
          columns={[
            {
              key: "member",
              header: "Member",
              sortValue: (r) => r.display_name,
              cell: (r) => (
                <div>
                  <p className="font-medium">{r.display_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.handle ? `@${r.handle}` : r.user_id.slice(0, 8)}
                  </p>
                </div>
              ),
            },
            {
              key: "groups",
              header: "Groups",
              cell: (r) =>
                r.groups.length ? (
                  <div className="flex flex-wrap gap-1">
                    {r.groups.map((g) => (
                      <StatusBadge key={g} status={g} />
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                ),
            },
            ...ROLES.map((role) => ({
              key: role,
              header: role,
              cell: (r: (typeof rows)[number]) => (
                <Switch
                  checked={r.roles.includes(role)}
                  onCheckedChange={(v) => mut.mutate({ userId: r.user_id, role, enabled: v })}
                />
              ),
            })),
            {
              key: "inspect",
              header: "",
              cell: (r) => (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setInspect(inspect === r.user_id ? null : r.user_id)}
                >
                  Permissions
                </Button>
              ),
            },
          ]}
        />
      )}
      {inspect && effective && (
        <div className="mt-4 card-surface p-4">
          <p className="text-sm font-semibold">Effective permissions</p>
          <p className="mt-1 text-xs text-muted-foreground">Roles: {effective.roles.join(", ") || "—"}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {effective.permissions.length ? (
              effective.permissions.map((p) => <StatusBadge key={p} status={p} />)
            ) : (
              <span className="text-xs text-muted-foreground">No grants.</span>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

/* -------------------------------- Groups -------------------------------- */

function GroupsTab() {
  const qc = useQueryClient();
  const list = useServerFn(admGroups);
  const staffFn = useServerFn(admStaff);
  const save = useServerFn(admSaveGroup);
  const remove = useServerFn(admDeleteGroup);
  const setGrant = useServerFn(admSetGrant);
  const setMember = useServerFn(admSetGroupMember);

  const [form, setForm] = useState({ key: "", name: "", description: "" });
  const [memberId, setMemberId] = useState<Record<string, string>>({});

  const { data = [], isLoading } = useQuery({ queryKey: ["adm-groups"], queryFn: () => list({ data: undefined }) });
  const { data: staff = [] } = useQuery({ queryKey: ["adm-staff"], queryFn: () => staffFn({ data: undefined }) });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["adm-groups"] });
  const fail = (e: unknown) => toast.error(e instanceof Error ? e.message : "Action failed.");

  const saveMut = useMutation({
    mutationFn: (input: { key: string; name: string; description?: string }) => save({ data: input }),
    onSuccess: () => {
      toast.success("Group saved.");
      setForm({ key: "", name: "", description: "" });
      refresh();
    },
    onError: fail,
  });
  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Group deleted.");
      refresh();
    },
    onError: fail,
  });
  const grantMut = useMutation({
    mutationFn: (input: { groupId: string; permission: string; enabled: boolean }) =>
      setGrant({ data: input }),
    onSuccess: refresh,
    onError: fail,
  });
  const memberMut = useMutation({
    mutationFn: (input: { groupId: string; userId: string; member: boolean }) =>
      setMember({ data: input }),
    onSuccess: () => {
      toast.success("Membership updated.");
      refresh();
    },
    onError: fail,
  });

  return (
    <div className="space-y-4">
      <Section title="New group" description="Bundle permissions and assign them to staff.">
        <div className="grid gap-3 sm:grid-cols-4">
          <Input
            placeholder="key (e.g. finance)"
            value={form.key}
            onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toLowerCase() }))}
          />
          <Input
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Input
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <Button
            variant="hot"
            disabled={saveMut.isPending || !form.key || !form.name}
            onClick={() =>
              saveMut.mutate({
                key: form.key,
                name: form.name,
                ...(form.description ? { description: form.description } : {}),
              })
            }
          >
            Create group
          </Button>
        </div>
      </Section>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data.map((raw) => {
        const g = raw as unknown as Record<string, unknown>;
        const id = String(g["id"]);
        const permissions = (g["permissions"] as string[]) ?? [];
        const members = (g["members"] as { user_id: string; display_name: string }[]) ?? [];
        return (
          <Section
            key={id}
            title={String(g["name"])}
            description={String(g["description"] ?? `Key: ${String(g["key"])}`)}
            actions={
              g["is_system"] ? (
                <StatusBadge status="system" />
              ) : (
                <Button size="sm" variant="ghost" onClick={() => delMut.mutate(id)}>
                  <Trash2 className="size-4" />
                </Button>
              )
            }
          >
            <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Permissions</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {PERMISSIONS.map((p) => (
                <label key={p} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                  <Switch
                    checked={permissions.includes(p)}
                    onCheckedChange={(v) => grantMut.mutate({ groupId: id, permission: p, enabled: v })}
                  />
                  <span className="font-mono text-xs">{p}</span>
                </label>
              ))}
            </div>

            <p className="mb-2 mt-4 text-xs font-semibold uppercase text-muted-foreground">Members</p>
            <div className="flex flex-wrap gap-2">
              {members.length === 0 && <span className="text-xs text-muted-foreground">No members.</span>}
              {members.map((m) => (
                <button
                  key={m.user_id}
                  type="button"
                  onClick={() => memberMut.mutate({ groupId: id, userId: m.user_id, member: false })}
                  className="rounded-full border px-3 py-1 text-xs hover:bg-muted"
                >
                  {m.display_name} ×
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <select
                className="rounded-md border bg-background px-2 py-1.5 text-sm"
                value={memberId[id] ?? ""}
                onChange={(e) => setMemberId((p) => ({ ...p, [id]: e.target.value }))}
              >
                <option value="">Add staff member…</option>
                {staff
                  .filter((s) => !members.some((m) => m.user_id === s.user_id))
                  .map((s) => (
                    <option key={s.user_id} value={s.user_id}>
                      {s.display_name}
                    </option>
                  ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                disabled={!memberId[id]}
                onClick={() =>
                  memberMut.mutate({ groupId: id, userId: memberId[id] as string, member: true })
                }
              >
                Add
              </Button>
            </div>
          </Section>
        );
      })}
    </div>
  );
}
